import React from 'react';

import filter from 'lodash/filter';
import {useSelector} from 'react-redux';

import {LinkPaginationRTK} from 'app/components/general/LinkPaginationRTK';
import {convertLocationParamsFoldersDocumentsToFetchParams} from 'app/documents/api';
import {CompactListFolderItem} from 'app/documents/components/CompactListFolderItem';
import {toggleDashboardRelation} from 'app/documents/slice';
import {useFolders} from 'app/folders/api';
import {selectIsHidingStackedDashboardFolders} from 'app/folders/slice';
import {KBTreeDeleteItemContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useAsyncActions} from 'app/utils/redux';

import type {DashboardLocationParamsType} from 'app/documents/types';
import type {WithRouterProps} from 'react-router';
import type {ContentType, FolderHashidSerializedType} from 'sweet-types';

type Props = {
  selectedFolder?: FolderHashidSerializedType;
} & WithRouterProps<unknown, DashboardLocationParamsType>;

export const FoldersPartialDashboard = ({
  selectedFolder,
  ...routerProps
}: Props) => {
  const isHidingStackedDashboardFolders = useSelector(
    selectIsHidingStackedDashboardFolders
  );
  const actions = useAsyncActions({toggleDashboardRelation});
  const toggleRelation = (contentType: ContentType, isOpen: boolean) =>
    actions.toggleDashboardRelation({
      contentType,
      isOpen,
    });

  const {
    data: folders,
    isLoading,
    refetch,
  } = useFolders(
    convertLocationParamsFoldersDocumentsToFetchParams(
      routerProps.location.query
    ),
    {skip: isHidingStackedDashboardFolders}
  );

  const foldersToDisplay = filter(folders?.results ?? [], (folder) => {
    if (routerProps.location.query.search != null) {
      return folder.name.indexOf(routerProps.location.query.search) !== -1;
    } else {
      return true;
    }
  });

  const activeRelations = useSelector((state) => state.documents.relations);

  useModelChangeSubscription((message, {runWhenAnyModelMatch}) => {
    runWhenAnyModelMatch(message, ['folder'], () => refetch());
  }, []);

  return (
    <>
      {!isHidingStackedDashboardFolders ? (
        <KBTreeDeleteItemContextProvider>
          {foldersToDisplay.map((folder) => (
            <CompactListFolderItem
              object={folder}
              allowedTeams={selectedFolder?.teams || []}
              key={`folder-${folder.id}`}
              toggleRelation={toggleRelation}
              activeRelations={activeRelations}
              refreshData={() => {
                refetch();
                return Promise.resolve(true);
              }}
            />
          ))}
        </KBTreeDeleteItemContextProvider>
      ) : null}

      {folders != null ? (
        <LinkPaginationRTK
          page={routerProps.location.query.folders_page}
          isLoading={isLoading}
          response={folders}
          pageParamName={'folders_page'}
        />
      ) : null}
    </>
  );
};
